import { FunctionalComponent, h } from "preact";
import { useContext, useState } from "preact/hooks";
import { makePollAnswerMsg } from "./InputUIService";
import { VoiceInButton } from "./voiceInButton";
import { log } from "../libs/QuerloLogger";
import {
  buildValidationRegExpFromServerPattern,
  isImageMimeType,
  parseDataType,
  storeFile,
} from "./OpenAnswerHelper";
import { useRef } from "preact/compat";
import {
  IcoAttachement,
  IcoCloudUpload,
  IcoSendButt,
} from "../components/icons";
import { LocalizationContext } from "../app";
import { TYPES } from "../libs/config";
import {escapeHTMLTags} from "../libs/stripHTML";

export const OpenAnswer: FunctionalComponent<{
  question: any;
  answerCallback: (answerRawObj: any) => void;
  voiceInputEnabled: boolean;
  backendBaseURL: string;
}> = ({ question, answerCallback, voiceInputEnabled, backendBaseURL }) => {
  const { t } = useContext(LocalizationContext);

  /* open answer related */
  const [inputVal, setInputVal] = useState(
    question.prefill ? question.prefill : ""
  );
  const [dataType, subDataType] = parseDataType(question.dataType);
  const validationPattern = buildValidationRegExpFromServerPattern(
    question.validation_format,
    dataType
  );

  /* upload answer / file-attachment related */
  const allowMultipleUploads = false;
  const fileInputField = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<Record<string, File>>({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);

  const isUploadEnabled =
    question.questionType === 1000 || question.questionType === 6;
  const isUploadQuestion = question.questionType === 6;

  function handleChange(e: Event): void {
    e.preventDefault();
    setInputVal((e.target as HTMLInputElement).value);
  }

  function handleFileSubmit(
    e: Event,
    fileType: string,
    storeFileBackendEndpoint: string
  ): void {
    e.preventDefault();

    /** this check should give true */
    if (Object.keys(selectedFiles).length === 0) {
      log.warn("No files selected");
      return;
    }

    if (isUploading) {
      log.warn("Upload is already in progress");
      return;
    }

    setIsUploading(true);

    /**
     * the way mimetype is handled restricts support to single file uploads only.
     * Must be refactored if we want to support multiple files uploads.
     */
    let uploadableFileMimeType = "";

    const uploadData = new FormData();
    for (const fName in selectedFiles) {
      uploadData.append("file", selectedFiles[fName]);
      uploadData.append("type", fileType);
      uploadableFileMimeType = selectedFiles[fName].type;
    }

    const uploadPromise: Promise<any> = new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.responseType = "json";
      xhr.upload.onprogress = (event: ProgressEvent): void => {
        setUploadPercent((event.loaded / event.total) * 100);
      };
      /*
                  xhr.addEventListener('progress', (event: ProgressEvent) => {
                      log.info('Progress', event);
                      const percentage = (event.loaded / event.total) * 100;
                      log.info(percentage); // Update progress here
                  })
      */

      xhr.onreadystatechange = (): void => {
        if (xhr.readyState !== 4) {
          return;
        }
        if (xhr.status !== 200) {
          reject();
        }
        resolve(xhr.response);
      };
      xhr.open("POST", `${backendBaseURL}upload/up5?json=1`, true);
      xhr.send(uploadData);
      //TODO must be cleaned up?
    });

    uploadPromise.then(
      (d: any) => {
        log.debug("uploadSuccess: ", d);
        storeFile(
          `${backendBaseURL}${storeFileBackendEndpoint}`,
          d.f_id,
          question.chat_id
        ).then(
          (r) => {
            r.json().then((responseObj: any) => {
              log.trace(responseObj);
              if (isUploadQuestion) {
                if (responseObj.s === 1) {
                  answerCallback(
                    makePollAnswerMsg(
                      {
                        // eslint-disable-next-line @typescript-eslint/camelcase
                        o_ans: responseObj.response.fileName,
                        ansID: 1,
                      },
                      question
                    )
                  );
                } else {
                  setIsUploading(false);
                  log.error("store uploaded file failed at application level.");
                }
              } else {
                /** upload attachment successfully finalized */
                answerCallback({
                  t: TYPES.CHAT_IMG_ATTACHMENT,
                  b: { f: responseObj.fileName, mt: uploadableFileMimeType },
                });
                setSelectedFiles({});
                setIsUploading(false);
              }
            });
          },
          () => {
            setIsUploading(false);
            log.error("store uploaded file failed at network level");
          }
        );
      },
      (d: boolean) => {
        //TODO handle server response for failure
        setIsUploading(false);
        log.error("upload failed", d);
      }
    );
  }

  function handleSubmit(e: Event): void {
    if (Object.keys(selectedFiles).length > 0) {
      handleFileSubmit(
        e,
        isUploadQuestion ? "fileUpload" : "ht",
        isUploadQuestion ? "storeUserFile" : "storeHTAttachment"
      );
      return;
    }

    e.preventDefault();
    log.debug(`Answer: ${inputVal}`);
    let answerShownInMsg = inputVal;
    let answerToBeStored = inputVal;
    setInputVal("");

    if (dataType === "password") {
      answerShownInMsg = "******";
    }
    if (subDataType === "countryNumber") {
      answerToBeStored =
        answerShownInMsg = `${question.countryCode}-${inputVal}`;
    }

    if (dataType === "text") {
      /** prevent XSS **/
      answerShownInMsg = escapeHTMLTags(inputVal);
    }

    const pollAnswerMsg = makePollAnswerMsg(
      {
        txt: answerShownInMsg,
        // eslint-disable-next-line @typescript-eslint/camelcase
        o_ans: answerToBeStored,
        ansID: -1,
      },
      question
    );

    answerCallback(pollAnswerMsg);
  }

  function handleVoiceIn(textInput: string): void {
    log.info("HANDLE VOICE IN");
    const voiceAnswerMsg = makePollAnswerMsg(
      {
        txt: textInput,
        // eslint-disable-next-line @typescript-eslint/camelcase
        o_ans: textInput,
        ansID: -1,
      },
      question
    );
    answerCallback(voiceAnswerMsg);
  }

  /** UPLOAD RELATED METHODS ***/

  function updateSelectedFiles(pickedFiles: FileList): void {
    setSelectedFiles((previousFilesState) => {
      const newFilesState = { ...previousFilesState };
      for (const file of pickedFiles as unknown as File[]) {
        if (!allowMultipleUploads) {
          log.debug("Selected a file: ", file);
          return { file };
        }
        newFilesState[file.name] = file;
      }
      return newFilesState;
    });
  }

  function removeSelectedFile(fName: string): void {
    setSelectedFiles((previousFilesState) => {
      const newFilesState = { ...previousFilesState };
      delete newFilesState[fName];
      return newFilesState;
    });
  }

  function handleFileChange(e: Event): void {
    e.preventDefault();
    const newFilesPickedFromGUI: FileList = (e.target as HTMLInputElement)
      .files as FileList;

    if (newFilesPickedFromGUI === null) {
      log.error("Internal error, no files selected");
      return;
    }
    updateSelectedFiles(newFilesPickedFromGUI);
  }

  // TODO is it better to lift createObjectURL up and avoid creating a new temp URL on every render,
  //  or can we just not mind?
  const previewContent = (
    <div className="previewWrapper">
      <ul>
        {Object.keys(selectedFiles).map((fName: string) => (
          // eslint-disable-next-line react/jsx-key
          <li className="previewSingle">
            {isImageMimeType(selectedFiles[fName].type) && (
              <img
                className={"imgPreview"}
                src={URL.createObjectURL(selectedFiles[fName])}
              />
            )}
            <figcaption>
              {selectedFiles[fName].name}
              {isUploading && uploadPercent && (
                <progress max="100" value={uploadPercent} />
              )}
            </figcaption>
            <a
              className="butt delete"
              onClick={() => {
                removeSelectedFile(fName);
              }}
            >
              <IcoCloudUpload />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <form
      name="inputForm"
      onSubmit={handleSubmit}
      role="form"
      className="{invalidSubmitted: showErr()}"
    >
      <div
        className={
          Object.keys(selectedFiles).length === 0
            ? "openAns"
            : "openAns selectedFile"
        }
      >
        <div className="uploader">{previewContent}</div>
        <div className="openAnsWrp">
          {Object.keys(selectedFiles).length === 0 &&
            (question.ml ? (
              <textarea
                placeholder={
                  question.placeholder || t("Write a message here...")
                }
                rows={3}
                id="chatInput"
                onChange={handleChange}
                /*            onKeyUp={(e): void => {
              if (e.key === "Enter") {
                handleSubmit(e);
              }
            }}*/
                autoComplete="off"
                required={true}
                aria-label="Answer input"
                aria-required="true"
              >
                {inputVal}
              </textarea>
            ) : (
              <input
                placeholder={
                  question.placeholder || t("Write a message here...")
                }
                id="chatInput"
                value={inputVal}
                onChange={handleChange}
                /*            onKeyUp={(e): void => {
                            if (e.key === "Enter") {
                              handleSubmit(e);
                            }
                          }}*/
                type={dataType}
                pattern={validationPattern}
                autoComplete="off"
                required={true}
                aria-label="Answer input"
                aria-required="true"
              />
            ))}
          {isUploadEnabled && (
            <input
              style={isUploadQuestion ? undefined : { display: "none" }}
              onChange={handleFileChange}
              ref={fileInputField}
              name={"fileInput"}
              type="file"
              title=""
              value=""
              autoComplete="off"
              required={isUploadQuestion}
            />
          )}
          {voiceInputEnabled && !isUploadEnabled && (
            <VoiceInButton cb={handleVoiceIn} />
          )}
          {isUploadEnabled && Object.keys(selectedFiles).length === 0 && (
            <button
              type="button"
              className="attachmentButt"
              onClick={(e): void => {
                e.preventDefault();
                fileInputField.current?.click();
              }}
            >
              <IcoAttachement />
              <span>{t("upload")}</span>
            </button>
          )}
          <button className="send">
            <IcoSendButt />
            <span>{t("send")}</span>
          </button>
        </div>
      </div>
    </form>
  );
};
